import {useState} from 'react'
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {KebabGlyph} from '@/components/ui/icons'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {EmployeeFormDialog} from '@/components/employee-form-dialog'
import {RegimeDriftBanner} from '@/components/regime-drift-banner'
import {DeclaredWorkerCount} from '@/components/declared-worker-count'
import {TagsManagerDialog} from '@/components/tags-manager-dialog'
import {InviteEmployeesDialog} from '@/components/invite-employees-dialog'
import {KairosMark} from '@/components/kairos-mark'
import {PendientesAprobacion} from '@/components/pendientes-aprobacion'
import {InactiveBadge} from '@/components/inactive-badge'
import {LicenciaBadge} from '@/components/licencia-badge'
import {ExternosAccesoSection} from '@/components/externos-acceso-section'
import {PlanLockedPage} from '@/components/plan-upsell'
import {useBusiness, useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {
  useAllEmployees,
  useEmployees,
  EMPLOYEES_PAGE_SIZE,
  type Employee,
} from '@/hooks/empresa/identidad/use-employees'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useSgsst} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {useTableState} from '@/hooks/common/use-table-state'
import {SearchInput} from '@/components/ui/search-input'

type TabKey = 'empleados' | 'externos'

function parseTab(value: string | null): TabKey | null {
  return value === 'empleados' || value === 'externos' ? value : null
}

function CargoCell({employee}: {employee: Employee}) {
  const {t} = useTranslation()
  const [first, ...rest] = employee.positions
  if (!first) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <CeldaEstado
      chip={
        <>
          <Tag tone="neutral" className="truncate">
            {first.name}
          </Tag>
          {rest.length > 0 && <Tag tone="neutral">{t('employees.table.cargoMore', {count: rest.length})}</Tag>}
        </>
      }
    />
  )
}

function EmployeeCard({
  employee,
  canEditEmployee,
  onOpenEdit,
  onOpen,
}: {
  employee: Employee
  canEditEmployee: boolean
  onOpenEdit: (employee: Employee) => void
  onOpen: () => void
}) {
  const {t} = useTranslation()
  return (
    <li
      className="surface-divider border-b px-4 py-3 transition-colors last:border-0 hover:bg-muted/55"
      data-testid="employee-card">
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
          <p className="truncate font-medium text-foreground">
            {employee.name} {employee.last_name}
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-xs text-muted-foreground">{employee.cedula}</span>
            <LicenciaBadge
              estado={employee.licencia_estado}
              categoria={employee.licencia_categoria}
              testId={`employee-card-licencia-${employee.id}`}
            />
          </p>
        </button>
        <div className="flex shrink-0 items-center gap-1" onClick={(evt) => evt.stopPropagation()}>
          {employee.status === 'active' ? (
            <Tag tone="neutral">{t('employees.status.active')}</Tag>
          ) : (
            <InactiveBadge since={employee.deactivated_at} />
          )}
          <RowMenu
            employee={employee}
            canEditEmployee={canEditEmployee}
            onOpenEdit={onOpenEdit}
            testIdPrefix="employee-card"
          />
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('employees.table.cargo')}
        </p>
        <CargoCell employee={employee} />
      </div>
    </li>
  )
}

function RowMenu({
  employee,
  canEditEmployee,
  onOpenEdit,
  testIdPrefix = 'employee',
}: {
  employee: Employee
  // El operador edita la membresía, nunca el rol: lo bloquean el diálogo y el servidor.
  canEditEmployee: boolean
  onOpenEdit: (employee: Employee) => void
  // Tarjetas y tabla se montan a la vez (corte por CSS): el prefijo mantiene único el testid.
  testIdPrefix?: string
}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  if (!canEditEmployee) return null
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('employees.rowMenu.open')}
          title={t('employees.rowMenu.open')}
          data-testid={`${testIdPrefix}-${employee.id}-menu`}
          onClick={(evt) => evt.stopPropagation()}
          className="border border-border">
          <KebabGlyph />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-1" onClick={(evt) => evt.stopPropagation()}>
        <button
          type="button"
          className="block w-full rounded-md px-2.5 py-1.5 text-left text-sm text-foreground hover:bg-muted"
          onClick={() => {
            setOpen(false)
            onOpenEdit(employee)
          }}>
          {t('employees.edit.title')}
        </button>
      </PopoverContent>
    </Popover>
  )
}

export function EmployeesPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const navigate = useNavigate()
  const table = useTableState('name')
  const godReadOnly = useGodModeReadOnly()
  const [editTarget, setEditTarget] = useState<Employee | null>(null)
  const [inviting, setInviting] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseTab(searchParams.get('tab')) ?? 'empleados'
  function setTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  const {data, isLoading, isError} = useEmployees(businessId, {
    page: table.page,
    search: table.search,
    sortBy: table.sortBy,
    sortDir: table.sortDir,
  })

  const capabilities = useCapabilities(businessId)
  const canOperate = capabilities.sgsst_operar
  const {data: business} = useBusiness(businessId)
  const isOwner = business?.role === 'owner'
  const canManagePeople = isOwner && !godReadOnly
  const canOperateEmployees = canOperate && !godReadOnly
  // Nómina completa: una persona pendiente puede no estar en la página visible.
  const {data: allEmployees} = useAllEmployees(canOperate ? businessId : undefined)
  const pendientes = allEmployees?.filter((e) => e.onboarding_pendiente_aprobacion) ?? []
  const {data: sgsst} = useSgsst(canOperate ? businessId : undefined)
  // Registrada la autoevaluación, el conteo del año se congela y el declarado se mantiene desde acá.
  const fase2 = !!sgsst?.assessment?.autoevaluacion_completed_at
  // Con la nómina vacía el primario vive dentro del vacío; buscando no cuenta como vacía.
  const listaVacia = !!data && data.items.length === 0 && !table.search

  if (isError) {
    return <p className="text-sm text-muted-foreground">{t('business.readOnlyNotice')}</p>
  }

  if (!capabilities.can_manage_employees) {
    return (
      <PlanLockedPage feature={t('plans.feature.employees')} businessId={businessId} businessName={business?.name} />
    )
  }

  const columns: Column<Employee>[] = [
    {
      key: 'name',
      header: t('employees.table.name'),
      sortable: true,
      width: 'w-40',
      cell: (e) => (
        <span className="block">
          <span className="block truncate font-medium text-foreground">
            {e.name} {e.last_name}
          </span>
          <LicenciaBadge
            estado={e.licencia_estado}
            categoria={e.licencia_categoria}
            className="mt-1"
            testId={`employee-row-licencia-${e.id}`}
          />
        </span>
      ),
    },
    {
      key: 'cedula',
      header: t('employees.table.cedula'),
      sortable: true,
      width: 'w-28',
      cell: (e) => <span className="block truncate font-mono text-xs text-muted-foreground">{e.cedula}</span>,
    },
    {
      key: 'cargo',
      header: t('employees.table.cargo'),
      width: 'w-40',
      cell: (e) => <CargoCell employee={e} />,
    },
    {
      key: 'status',
      header: t('employees.table.status'),
      sortable: true,
      width: 'w-28',
      cell: (e) =>
        e.status === 'active' ? (
          <CeldaEstado
            chip={
              <>
                <Tag tone="neutral">{t('employees.status.active')}</Tag>
                {e.verificacion_identidad ? (
                  <Tag
                    tone={e.verificacion_identidad === 'pendiente' ? 'amber' : 'red'}
                    data-testid={`employee-verificacion-${e.id}`}>
                    {t(`verificacionIdentidad.chip.${e.verificacion_identidad}`)}
                  </Tag>
                ) : (
                  !e.tiene_acceso && <Tag tone="amber">{t('employees.acceso.pending')}</Tag>
                )}
              </>
            }
          />
        ) : (
          <CeldaEstado chip={<InactiveBadge since={e.deactivated_at} testId="employee-inactive-badge" />} />
        ),
    },
    {
      key: 'menu',
      header: '',
      width: 'w-10',
      className: 'text-right',
      cell: (e) => (
        <div onClick={(evt) => evt.stopPropagation()}>
          <RowMenu employee={e} canEditEmployee={canOperateEmployees} onOpenEdit={setEditTarget} />
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <RegimeDriftBanner businessId={businessId!} linkToSgsst frozen={fase2} />
      <div className="flex items-center gap-2 text-foreground">
        <KairosMark />
        <h1 className="font-heading text-xl">{t('employees.title')}</h1>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="empleados" data-testid="tab-empleados">
            {t('employees.tabs.empleados')}
          </TabsTrigger>
          <TabsTrigger value="externos" data-testid="tab-externos">
            {t('employees.tabs.externos')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="empleados" className="space-y-5 pt-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            {(canManagePeople || canOperateEmployees) && (
              <div className="flex flex-wrap items-center gap-2">
                {canOperate && (
                  <Button size="sm" variant="outline" asChild data-testid="open-examenes-matriz">
                    <Link to={`/businesses/${businessId}/examenes`}>{t('examenesMatriz.openCta')}</Link>
                  </Button>
                )}
                {canOperateEmployees && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="open-invite-employees"
                    onClick={() => setInviting(true)}>
                    {t('employees.invite')}
                  </Button>
                )}
                {canOperateEmployees && (
                  <TagsManagerDialog
                    businessId={businessId!}
                    trigger={
                      <Button size="sm" variant="outline" data-testid="open-tags-manager">
                        {t('tags.manage')}
                      </Button>
                    }
                  />
                )}
                {canOperateEmployees && !listaVacia && (
                  <EmployeeFormDialog
                    businessId={businessId!}
                    canAssignRole={isOwner}
                    trigger={
                      <Button size="sm" data-testid="nav-empleados">
                        {t('employees.new')}
                      </Button>
                    }
                  />
                )}
              </div>
            )}
          </div>

          {canOperate && (
            <PendientesAprobacion
              businessId={businessId!}
              pendientes={pendientes}
              canEdit={canOperateEmployees}
              canAssignResponsable={isOwner}
            />
          )}

          {canOperate && (
            <DeclaredWorkerCount
              businessId={businessId!}
              declaredCount={business?.declared_worker_count ?? null}
              activeCount={sgsst?.active_employee_count ?? 0}
              fase2={fase2}
              // PATCH /businesses es solo del dueño; el operador ajusta el conteo desde la autoevaluación.
              editable={canManagePeople}
            />
          )}

          <div className="surface overflow-hidden">
            <div className="surface-divider border-b px-4 py-3">
              <SearchInput
                value={table.searchInput}
                onChange={table.setSearchInput}
                placeholder={t('employees.searchPlaceholder')}
              />
            </div>

            {isLoading && !data ? (
              <div className="p-4">
                <Skeleton className="h-64 w-full" />
              </div>
            ) : data && data.items.length === 0 ? (
              <div className="px-6 py-14 text-center">
                <p className="font-heading text-lg text-foreground">
                  {table.search ? t('employees.noResults') : t('employees.emptyTitle')}
                </p>
                {!table.search && canOperateEmployees && (
                  <div className="mt-4 flex justify-center">
                    <EmployeeFormDialog
                      businessId={businessId!}
                      canAssignRole={isOwner}
                      trigger={
                        <Button size="sm" data-testid="employees-empty-cta">
                          {t('employees.new')}
                        </Button>
                      }
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                <ul className="lg:hidden">
                  {(data?.items ?? []).map((e) => (
                    <EmployeeCard
                      key={e.id}
                      employee={e}
                      canEditEmployee={canOperateEmployees}
                      onOpenEdit={setEditTarget}
                      onOpen={() => navigate(`/businesses/${businessId}/empleados/${e.id}`)}
                    />
                  ))}
                </ul>
                <div className="hidden px-1 lg:block">
                  <DataTable
                    columns={columns}
                    rows={data?.items ?? []}
                    rowKey={(e) => e.id}
                    sortBy={table.sortBy}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    onRowClick={(e) => navigate(`/businesses/${businessId}/empleados/${e.id}`)}
                  />
                </div>
                {!!data?.total && (
                  <div className="surface-divider border-t px-4 py-2.5">
                    <PaginationBar
                      page={table.page}
                      pageSize={EMPLOYEES_PAGE_SIZE}
                      total={data.total}
                      onPageChange={table.setPage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="externos" className="space-y-8 pt-4">
          <ExternosAccesoSection businessId={businessId!} canManage={canManagePeople} />
        </TabsContent>
      </Tabs>

      {editTarget && (
        <EmployeeFormDialog
          businessId={businessId!}
          employee={editTarget}
          canAssignRole={isOwner}
          open={!!editTarget}
          onOpenChange={(open) => !open && setEditTarget(null)}
        />
      )}
      <InviteEmployeesDialog
        businessId={businessId!}
        canEdit={canOperateEmployees}
        open={inviting}
        onOpenChange={setInviting}
      />
    </div>
  )
}
